import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberPolicyRepairPreview, MemberPolicyRepairResult } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
export function MemberPolicyRepair() {
  const { t } = useTranslation('member');
  const session = useAuthStore((s) => s.sessionRevision);
  const identity = useAuthStore((s) => `${s.user?.id}:${s.sessionRevision}`);
  const canManage = useAuthStore((s) => s.allowedActions?.includes('member:level_manage'));
  const { data } = useQuery({
    queryKey: ['member-admin-tools', identity],
    queryFn: () => api.get<{ policyRepairEnabled: boolean }>('/members/admin-tools'),
    enabled: !!canManage,
  });
  const query = useQueryClient();
  const [preview, setPreview] = useState<MemberPolicyRepairPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    setPreview(null);
    setMessage('');
  }, [identity]);
  if (!canManage || !data?.policyRepairEnabled) return null;
  async function load() {
    setBusy(true);
    setMessage('');
    try {
      const next = await api.post<MemberPolicyRepairPreview>(
        '/members/levels/policies/repair/preview',
        { operationKey: crypto.randomUUID() },
      );
      if (useAuthStore.getState().sessionRevision === session) setPreview(next);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : t('adminTools.failed'));
    } finally {
      setBusy(false);
    }
  }
  async function apply() {
    if (!preview) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await api.post<MemberPolicyRepairResult>(
        '/members/levels/policies/repair/apply',
        { operationKey: preview.operationKey, previewToken: preview.previewToken },
      );
      if (useAuthStore.getState().sessionRevision !== session) return;
      setMessage(t('adminTools.repaired', { count: result.inserted }));
      setPreview(null);
      await query.invalidateQueries({ queryKey: ['member-levels'] });
    } catch (e) {
      setMessage(e instanceof Error ? e.message : t('adminTools.failed'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="my-4 rounded-lg border border-base-300 p-4">
      <h2 className="font-semibold">{t('adminTools.repairTitle')}</h2>
      <p className="text-sm my-2">{t('adminTools.defaults')}</p>
      <button className="btn btn-outline" disabled={busy} onClick={() => void load()}>
        {t('adminTools.previewRepair')}
      </button>
      {message && <p role="status">{message}</p>}
      {preview && (
        <div>
          <p>{t('adminTools.missing', { count: preview.missing.length })}</p>
          {preview.conflicts.length > 0 && (
            <p role="alert" className="text-error">
              {t('adminTools.conflicts', { count: preview.conflicts.length })}
            </p>
          )}
          <ul>
            {preview.missing.map((pair) => (
              <li key={`${pair.venueId}:${pair.levelId}`}>
                {pair.venueName} · {pair.levelName}
              </li>
            ))}
          </ul>
          <button
            className="btn btn-primary"
            disabled={busy || !!preview.conflicts.length || !preview.missing.length}
            onClick={() => void apply()}
          >
            {t('adminTools.applyRepair')}
          </button>
        </div>
      )}
    </section>
  );
}
